import * as React from 'react';
import { MdAdd } from 'react-icons/md';
import { AddButton, Options } from 'ui';
import { SelectMultipleHierarchy } from 'app/area/shared/translated-components';
import { OpenStateInjectedProps, withOpenState } from 'ui/common/enhancers/with-open-state';
import { CloseOnBlur } from 'ui/common/close-on-blur';
import { withOptionCategorySupport } from 'app/components/enhancers/with-option-category-support';
import { DialogExpander } from 'ui/common/dialog-expander/dialog-expander';
import { AddDocumentItemOptions } from 'app/infrastructure/ui/form/types';
import styles from './document-item-from-multiselect.scss';
import { ComplexValue } from 'app/types-shared';
import { keyCodeTypes } from 'ui/common/key-code-types';
import { HotKeys } from 'react-hotkeys';

export interface DocumentItemFromMultiSelectProps extends Options<number> {
  textAddItem: string;
  textDone: string;
  textCodeSelectorTitle: string;
  required?: boolean;
  minLevel?: number;
  existingKeys: ComplexValue<number>[];
  onAddDocumentItem?: (selectAddedItem?: boolean, options?: AddDocumentItemOptions) => void;
  onAddDocumentItems?: (selectAddedItem?: boolean, options?: AddDocumentItemOptions[]) => void;
  placeholderSearch?: string;
  leafNodeOnly?: boolean;
  extraFooterButton?: (onCloseCallback: (onClose: void) => void) => JSX.Element;
  canHideSearchInput?: boolean;
}

interface DocumentItemFromMultiSelectState {
  selectedItems: ComplexValue<number>[];
}

/**
 * This component implements a common pattern for subtables of a case:
 * when the keys in a subtable are codes and the selection should be limited by existing keys.
 */
class DocumentItemFromMultiSelectComponent
  extends React.PureComponent<DocumentItemFromMultiSelectProps & OpenStateInjectedProps, DocumentItemFromMultiSelectState> {
  private keyMap: any;
  private handlers: any;

  public constructor(props: DocumentItemFromMultiSelectProps & OpenStateInjectedProps) {
    super(props);
    this.state = {
      selectedItems: []
    };

    this.keyMap = {
      handleClose: [keyCodeTypes.Esc],
      handleEnter: [keyCodeTypes.Enter]
    };

    this.handlers = {
      handleClose: this.onClose,
      handleEnter: this.onClose
    };
  }

  private onCodesSelected = (codes: ComplexValue<number>[]) => {
    this.setState({
      selectedItems: codes
    });
  };

  private onConfirm = () => {
    const codes = this.state.selectedItems.map(c => {
      return {
        childId: c.value,
        initialValues: { primaryKey: c }
      };
    });
    if (!!codes && codes.length) {
      this.props.onAddDocumentItem && codes.forEach(c => this.props.onAddDocumentItem(false, c));
      this.props.onAddDocumentItems && this.props.onAddDocumentItems(false, codes);
    }
    this.clearSelectedItems();
  };

  private onClose = () => {
    const { close } = this.props;

    this.onConfirm();

    close();
  };

  private openCodeSelector = (event: any) => {
    if (!this.props.isOpen) {
      this.props.open();
    }
  };

  private clearSelectedItems = () => {
    if (this.props.isOpen) {
      this.setState({
        selectedItems: []
      });
    }
  };

  private excludeSelectedOptions = (options: ComplexValue<number>[]): ComplexValue<number>[] => {
    let returnedOptions: ComplexValue<number>[] = [];

    returnedOptions = options.filter(c => this.props.existingKeys.findIndex(key => key.value === c.value) < 0)
      .map(element => {
        if (!!element.descendants) {
          return {
            ...element,
            descendants: this.excludeSelectedOptions(element.descendants)
          };
        }
        return element;
      });
    return returnedOptions;
  };

  private renderCodeSelector() {
    const {
      textDone,
      textCodeSelectorTitle,
      minLevel,
      options,
      onLoadOptions,
      fetching,
      loadOptions,
      loadOptionsError,
      placeholderSearch,
      isClosing,
      leafNodeOnly,
      extraFooterButton,
      canHideSearchInput = true,
      displaySelectAll = false,
      locale
    } = this.props;

    const filteredOptions = options && this.excludeSelectedOptions(options);
    const allOptionsSelected = !(filteredOptions && filteredOptions.length > 0);
    const hideSearchInput = canHideSearchInput && allOptionsSelected && !(loadOptions && loadOptions.query);
    const closingAnimation = isClosing ? 'animate-out' : '';

    return (
      <div className={closingAnimation}>
        <DialogExpander
          onClose={this.onClose}
          title={textCodeSelectorTitle}
          textDone={textDone}
          selectExpander={true}
          extraFooterActions={[extraFooterButton?.(this.onClose)]}>
          <SelectMultipleHierarchy
            componentId={'multi-select'}
            className={null}
            onLoadOptions={onLoadOptions}
            options={filteredOptions}
            valueItems={this.state.selectedItems}
            onChange={this.onCodesSelected}
            alwaysOpen={true}
            fetching={fetching}
            minLevel={minLevel}
            loadOptions={loadOptions}
            loadOptionsError={loadOptionsError}
            placeholder={placeholderSearch}
            hideSearchInput={hideSearchInput}
            leafNodeOnly={leafNodeOnly}
            extraFooterButton={extraFooterButton}
            displaySelectAll={displaySelectAll}
            locale={locale}
          />
        </DialogExpander>
      </div>
    );
  }

  public render() {
    const { required, isOpen, textAddItem } = this.props;
    const { documentItemFromMultiSelect, btnWrapper } = styles;

    return (
      <CloseOnBlur onBlur={this.onClose}>
        <HotKeys keyMap={this.keyMap} handlers={this.handlers}>
          <div className={documentItemFromMultiSelect}>
            <div className={btnWrapper}>
              <AddButton icon={MdAdd}
                onClick={this.openCodeSelector}
                required={required}>
                {textAddItem}
              </AddButton>
            </div>
            {isOpen && this.renderCodeSelector()}
          </div>
        </HotKeys>
      </CloseOnBlur>
    );
  }
}

const l = withOpenState(true)(DocumentItemFromMultiSelectComponent);
const l2 = withOptionCategorySupport(l);

export { l2 as DocumentItemFromMultiSelect };
